/**
 * Trucker Count Page (Iteration 12 — N10)
 * 
 * Mobile-first, inside TruckerShell.
 * Step 1: Select project (large tap cards)
 * Step 2: Select scope → location (cascading pickers)
 * Step 3: Enter count (InlineCountForm)
 * Step 4: Confirm + submit
 * 
 * LAT-25: below_zero validation names current manual bucket count
 * LAT-26: double-submit prevention; form stays populated on failure
 * LAT-27: manual entry writes to manual bucket only; beacon unchanged
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import TruckerShell from '../elements/TruckerShell';
import SegmentedControl from '../elements/SegmentedControl';
import InlineCountForm from '../elements/InlineCountForm';

import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { useCapability } from '../../hooks/useCapability';
import { fetchProjectsByCustomer } from '../../store/slices/projectSlice';
import { getScopes, getCounts } from '../../fixtures/seedData';
import { resolveStatusTone } from '../../utils/statusTone';

const STEPS = ['Project', 'Scope & Location', 'Enter Count', 'Confirm & Submit'];

const TruckerCountPage = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const capability = useCapability();
  const activeCustomer = useAppSelector((state) => state.customer.activeCustomer);
  const projectList = useAppSelector((state) => state.project.projectList) || [];

  const [activeStep, setActiveStep] = useState(0);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [scopes, setScopes] = useState([]);
  const [selectedScope, setSelectedScope] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentCounts, setCurrentCounts] = useState({ supplyManual: 0, demandManual: 0 });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [lastEntry, setLastEntry] = useState(null);

  // Load projects via Redux
  useEffect(() => {
    if (!activeCustomer?.id) return;
    dispatch(fetchProjectsByCustomer(activeCustomer.id));
  }, [dispatch, activeCustomer?.id]);

  // Derive manual/hybrid projects from Redux store
  useEffect(() => {
    const manualProjects = projectList.filter(
      (p) => p.trackingMode === 'manual' || p.trackingMode === 'hybrid'
    );
    setProjects(manualProjects);

    // Auto-select if single project
    if (manualProjects.length === 1) {
      setSelectedProject(manualProjects[0]);
      setActiveStep(1);
    }
  }, [projectList]);

  // Load scopes when project selected
  useEffect(() => {
    if (!selectedProject) return;
    const result = getScopes(selectedProject.id);
    setScopes(result.items || []);
  }, [selectedProject?.id]);

  // Load current counts when location selected
  useEffect(() => {
    if (!selectedProject || !selectedLocation) return;
    const countsResult = getCounts(selectedProject.id);
    const locationCount = (countsResult.items || []).find(
      (c) => c.locationId === selectedLocation.id
    );
    setCurrentCounts({
      supplyManual: locationCount?.supplyManual || 0,
      demandManual: locationCount?.demandManual || 0,
    });
  }, [selectedProject?.id, selectedLocation?.id]);

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setSelectedScope(null);
    setSelectedLocation(null);
    setActiveStep(1);
  };

  const handleSelectScope = (scope) => {
    setSelectedScope(scope);
    setSelectedLocation(null);
  };

  const handleSelectLocation = (location) => {
    setSelectedLocation(location);
    setActiveStep(2);
  };

  const handleSubmitCount = async ({ supplyDelta, demandDelta, idempotencyKey }) => {
    setSubmitLoading(true);
    setSubmitError(null);

    try {
      // TODO: Wire to API — submitEntry with channel: 'trucker-app'
      console.warn('[FIXTURE] Trucker count entry:', {
        projectId: selectedProject.id,
        scopeId: selectedScope.id,
        locationId: selectedLocation.id,
        supplyDelta,
        demandDelta,
        channel: 'trucker-app',
        idempotencyKey,
      });

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      setLastEntry({ supplyDelta, demandDelta });
      setActiveStep(3);
    } catch (err) {
      setSubmitError(err.message || 'Could not submit — check your connection');
      throw err; // Re-throw so InlineCountForm keeps form populated (LAT-26)
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEnterAnother = () => {
    setSelectedLocation(null);
    setSelectedScope(null);
    setLastEntry(null);
    setActiveStep(1);
  };

  const handleDone = () => {
    setSelectedProject(null);
    setSelectedScope(null);
    setSelectedLocation(null);
    setLastEntry(null);
    setActiveStep(0);
  };

  return (
    <TruckerShell>
      <Box sx={{ maxWidth: 500, mx: 'auto', p: 3 }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0: Select Project */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>Select Project</Typography>
            {projects.length === 0 ? (
              <Alert severity="info">
                No projects assigned — contact your administrator
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {projects.map((project) => (
                  <Card key={project.id} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                    <CardActionArea
                      onClick={() => handleSelectProject(project)}
                      sx={{ minHeight: theme.spacing(11) }}
                    >
                      <CardContent>
                        <Typography variant="h6">{project.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {resolveStatusTone('capability', project.trackingMode).label}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Step 1: Select Scope → Location */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {selectedProject?.name} — Select Location
            </Typography>

            {/* Scope picker */}
            {scopes.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Scope
                </Typography>
                <SegmentedControl
                  options={scopes.map((s) => ({ value: s.id, label: s.name }))}
                  value={selectedScope?.id || null}
                  onChange={(val) => {
                    const scope = scopes.find((s) => s.id === val);
                    handleSelectScope(scope);
                  }}
                  size="small"
                  fullWidth
                />
              </Box>
            )}

            {/* Location picker */}
            {selectedScope && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Location
                </Typography>
                {(selectedScope.locations || []).length === 0 ? (
                  <Alert severity="info">No locations in this scope</Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(selectedScope.locations || []).map((loc) => (
                      <Card key={loc.id} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                        <CardActionArea
                          onClick={() => handleSelectLocation(loc)}
                          sx={{ minHeight: theme.spacing(11) }}
                        >
                          <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body1" fontWeight={500}>{loc.name}</Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Button
              onClick={() => setActiveStep(0)}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              ← Back to projects
            </Button>
          </Box>
        )}

        {/* Step 2: Enter Count */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Enter Count
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {selectedScope?.name} → {selectedLocation?.name}
            </Typography>

            <InlineCountForm
              supplyMode={capability.supplyMode}
              onSubmit={handleSubmitCount}
              loading={submitLoading}
              error={submitError}
              currentCounts={currentCounts}
            />

            <Button
              onClick={() => setActiveStep(1)}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              ← Back to locations
            </Button>
          </Box>
        )}

        {/* Step 3: Done */}
        {activeStep === 3 && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
            <Typography variant="h6" gutterBottom>Count Submitted</Typography>
            {lastEntry && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Supply: {lastEntry.supplyDelta > 0 ? '+' : ''}{lastEntry.supplyDelta},
                Demand: {lastEntry.demandDelta > 0 ? '+' : ''}{lastEntry.demandDelta}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleEnterAnother}
                sx={{ minHeight: theme.spacing(12), textTransform: 'none', fontWeight: 600 }}
              >
                Enter Another
              </Button>
              <Button
                variant="outlined"
                onClick={handleDone}
                sx={{ minHeight: theme.spacing(12), textTransform: 'none' }}
              >
                Done
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </TruckerShell>
  );
};

export default TruckerCountPage;
